// Colour, radius and type tokens. `C` is the live palette: NudgeApp swaps
// LIGHT or DARK into it (Object.assign) when the theme changes, so every
// importer sees the current values at render time.
export const LIGHT = {
  // ─── V2.8 Light Theme ─────────────────────────────────────────────
  bg: "#F5F4F0", surface: "#FFFFFF", surface2: "#ECEAE3", cardTint: "#FFFFFF",
  backdrop: "#E6E3DA", // either side of the app on a wide screen: a shade darker than bg
  border: "rgba(0,0,0,0.14)", borderCard: "rgba(0,0,0,0.36)",
  accent: "#0F6E56", onAccent: "#FFFFFF", // onAccent: text on an accent fill (6.2:1)
  onTier: "#FFFFFF", // text on a TIER or goal colour, which are mid tones in both themes
  accentLight: "rgba(15,110,86,0.12)", accentMid: "rgba(15,110,86,0.22)",
  text: "#1A1A1A", textMid: "rgba(28,28,28,0.68)", textDim: "rgba(28,28,28,0.63)", // 4.6:1 or better on every light surface (was 0.45, 2.8:1)
  textGreen: "#0F6E56", textGreenLight: "rgba(15,110,86,0.11)",
  red: "#A32D2D",     redLight: "#F7C1C1",     redBg: "#FCEBEB",
  amber: "#854F0B",   amberLight: "#FAC775",   amberBg: "#FAEEDA",
  orange: "#A3420F",  orangeLight: "#F6B08A",  orangeBg: "#FCE7DA", // Home's Key Dates tile, like its Plan row
  blue: "#185FA5",    blueLight: "#85B7EB",    blueBg: "#E6F1FB",
  teal: "#0F6E56",    tealLight: "#5DCAA5",    tealBg: "#E1F5EE",
  purple: "#534AB7",  purpleLight: "#AFA9EC",  purpleBg: "#EEEDFE",
  cyan: "#0E6F86",    cyanLight: "#7FCFE0",    cyanBg: "#E0F3F7",
  // Today's Inspiration: its body, and the cards inside it (Craig, 24 Oct 2026: "The content within the fold seems
  // too dark overall"). Light: a near-white body under white cards. Dark: the cards are the lightest grey the green
  // labels on them stay readable on (4.5:1), on the card colour behind.
  foldBody: "#FAF9F6", foldCard: "#FFFFFF",
  pink: "#A3245E",    pinkLight: "#F2B3CF",    pinkBg: "#FCE8F1", // Home's Gratitude tile
  lime: "#4B6A10",    limeLight: "#C2DD86",    limeBg: "#EEF5DC", // Home's Temptations tile
  gold: "#765A00",    goldLight: "#E9CF6A",    goldBg: "#FBF3D3", // Home's Today tile
  forest: "#2D6A2F",  forestLight: "#94CC98",  forestBg: "#E2F2E3", // Home's Radar tile
  shadow: "0 1px 6px rgba(0,0,0,0.07)", shadowMd: "0 4px 20px rgba(0,0,0,0.11)",
  // Words and marks on a goal's colour bar (Goals): the bars are the same in both themes.
  onGoal: "#fff", onGoalDim: "rgba(255,255,255,0.9)",
  scrim: "rgba(0,0,0,0.45)", sheetShadow: "0 -4px 24px rgba(0,0,0,0.22)", // behind and around a bottom sheet
  r: "18px", rsm: "11px",
  fsXs: 11, fsSm: 13, fsMd: 15, fsLg: 17, fsXl: 20, fs2xl: 24, fs3xl: 28,
};

export const DARK = {
  // ─── V2.8 Dark Theme ──────────────────────────────────────────────
  bg: "#1C1C1E", surface: "#3A3A3C", surface2: "#48484A", cardTint: "#3A3A3C",
  backdrop: "#111113",
  border: "rgba(255,255,255,0.13)", borderCard: "rgba(255,255,255,0.22)",
  accent: "#1D9E75", onAccent: "#1C1C1E", // white on this green is 3.4:1; this is 5.0:1
  onTier: "#FFFFFF",
  accentLight: "rgba(29,158,117,0.25)", accentMid: "rgba(29,158,117,0.40)",
  text: "#F0EFE9", textMid: "rgba(240,239,233,0.72)", textDim: "rgba(240,239,233,0.60)", // 6.1:1 on the page, 4.7:1 on cards (was 0.50)
  textGreen: "#5DCAA5", textGreenLight: "rgba(93,202,165,0.18)",
  red: "#F09595",     redLight: "#6B1F1F",     redBg: "#4A1515",
  amber: "#EF9F27",   amberLight: "#9B5E0A",   amberBg: "#4A2D05",
  orange: "#F2834A",  orangeLight: "#A0441A",  orangeBg: "#4A1E08",
  blue: "#378ADD",    blueLight: "#185FA5",    blueBg: "#0A2E52",
  teal: "#5DCAA5",    tealLight: "#1D9E75",    tealBg: "#0A3D2E",
  purple: "#AFA9EC",  purpleLight: "#534AB7",  purpleBg: "#2A2060",
  cyan: "#5CC8DF",    cyanLight: "#1A6E80",    cyanBg: "#0B3440",
  foldBody: "#3A3A3C", foldCard: "#48484A",
  pink: "#F290BD",    pinkLight: "#7A2350",    pinkBg: "#4A1530",
  lime: "#B3D66A",    limeLight: "#4B6A10",    limeBg: "#28370A",
  gold: "#EBCB55",    goldLight: "#7D6300",    goldBg: "#3A2F05",
  forest: "#86CF8B",  forestLight: "#2F6E33",  forestBg: "#14361A",
  shadow: "0 1px 6px rgba(0,0,0,0.30)", shadowMd: "0 4px 20px rgba(0,0,0,0.45)",
  // Words and marks on a goal's colour bar (Goals): the bars are the same in both themes.
  onGoal: "#fff", onGoalDim: "rgba(255,255,255,0.9)",
  scrim: "rgba(0,0,0,0.45)", sheetShadow: "0 -4px 24px rgba(0,0,0,0.22)", // behind and around a bottom sheet
  r: "18px", rsm: "11px",
  fsXs: 11, fsSm: 13, fsMd: 15, fsLg: 17, fsXl: 20, fs2xl: 24, fs3xl: 28,
};

// Solid colours that white text sits on, the same in both themes. Only Key Dates' is left since My Plan, which
// had a row colour for each part of the plan, was parked on parked/plan-page (23 Oct 2026).
export const TIER = {
  keyDates: "#A8551A",
};

// C is a mutable theme object: NudgeApp's theme effect (and the pre-auth render) swap LIGHT or DARK into it
export const C = { ...LIGHT };
